/**
 * @file config.ts
 *
 * This file contains the i18n (internationalization) configuration for the frontend application.
 * It initializes and configures i18next with language detection, resource loading, and integration with React.
 *
 * - Loads Finnish and Swedish translation resources.
 * - Uses browser language detection (via cookies and HTML tag).
 * - Maps detected locales to supported application languages.
 * - Sets up i18next for use with React components.
 * - Handles initialization success and error logging.
 */
import i18n from 'i18next';
import I18nextBrowserLanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import translationFI from './fi/translation.json';
import translationSV from './sv/translation.json';
import translationEN from './en/translation.json';
import { Language } from '../utils/constants';
import { mapLocaletoLanguage } from '../utils/i18n';

i18n
  .use(I18nextBrowserLanguageDetector)
  .use(initReactI18next)
  .init({
    debug: true,
    resources: {
      fi: {
        common: translationFI
      },
      sv: {
        common: translationSV
      },
      en: {
        common: translationEN
      }
    },
    fallbackLng: Language.fi,
    supportedLngs: Object.keys(Language),
    detection: {
      // The order prioritizes cookie over htmlTag to ensure the user agent language is the active language.
      // In op.fi when the user logs in, op.login.language cookie is set to the current language
      order: ['cookie', 'htmlTag'],
      lookupCookie: 'GUEST_LANGUAGE_ID',
      // Op.fi sets the HTML lang attribute as <lang_variant> but we (and OPUX components) support just <lang>,
      // so we want to strip the (possible) variant out of the detected language and use the languages
      // we have defined to be supported in the application.
      convertDetectedLanguage: mapLocaletoLanguage
    },
    ns: ['common'],
    defaultNS: 'common'
  })
  .then(() => {
     /* no-op */
  })
  .catch(error => {
    console.error('Error initializing i18next:', error);
  });
